import { useState, useEffect } from 'react'
import { apiClient, useAuthStore } from '@indipia/shared'
import type { SiteInfo } from './CalendarMultiView'
import { useCommonCodes } from '../context/CommonCodesContext'
import { Alert, Badge, Button, Combobox, Select, Textarea, DatePicker } from '@indipia/ui-core'
import { useModalA11y } from '../hooks/useModalA11y'
import { Z_POPUP } from '../constants/zIndex'
import { usePeriodSyncConfirm } from '../hooks/usePeriodSyncConfirm'
import { useSiteRefresh } from '../context/SiteRefreshContext'
import { computePeriodSyncPlan } from '../constants/periodSync.logic'

interface Partner {
  partnerId: number
  partnerName: string
  mobile?: string
}

type Props = {
  onClose: () => void
  selectedSite: SiteInfo
  partners: Partner[]
  defaultStartDate?: string
  defaultWorkTypeCode?: string
  /** 이 스케줄(간트)에 이미 있는 공종 코드 — 공종 목록에서 '이미 있음' 배지로 알린다(추가는 막지 않음) */
  existingWorkTypeCodes?: readonly (string | null | undefined)[]
  onSaved: () => void
}

const labelStyle: React.CSSProperties = {
  fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--shell-on-surface-2)', display: 'block', marginBottom: 5,
}

function todayISO() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
}

function addCalendarDays(iso: string, n: number): string {
  const d = new Date(iso)
  d.setDate(d.getDate() + n)
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
}

export function AddTaskPopup({ onClose, selectedSite, partners, defaultStartDate, defaultWorkTypeCode, existingWorkTypeCodes, onSaved }: Props) {
  const user = useAuthStore(s => s.user)
  const { codes } = useCommonCodes()
  const { boxRef, titleId } = useModalA11y(true, onClose)
  // 공종이 스케줄 기간을 넘으면 '함께 바뀝니다' 확인 후 저장(결정 3~5) — 종료일 당일 하드 가드는 두지 않는다
  const periodSync = usePeriodSyncConfirm()
  const refreshSites = useSiteRefresh()

  const startDate = defaultStartDate ?? todayISO()

  const [form, setForm] = useState({
    workTypeCode: defaultWorkTypeCode ?? '',
    foremanName: '',
    taskStartDate: startDate,
    taskEndDate: startDate,
    description: '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  // 공종별 기본 소요일(설정 화면에서 저장된 값) — 시작일 선택 시 종료일 기본값 계산에 쓴다.
  const [durationsByCode, setDurationsByCode] = useState<Map<string, number>>(new Map())
  // '자재파트너가 시공' 공종 코드 집합 — 해당 공종은 시공파트너(작업반장)를 지정하지 않는다
  const [constructCodes, setConstructCodes] = useState<Set<string>>(new Set())
  const isConstructByVendor = constructCodes.has(form.workTypeCode)

  const headers = {
    ...(user?.compId   ? { 'X-Comp-Id': String(user.compId) } : {}),
    ...(user?.memberId ? { 'X-User-Id': String(user.memberId) } : {}),
  }

  useEffect(() => {
    apiClient.get<{ data: { workTypeDurations: { workTypeCode: string; duration: number }[]; orderSettings?: { workTypeCode: string; constructByVendorYn?: string | null }[] } }>(
      '/schedule/settings', { headers: { ...(user?.compId ? { 'X-Comp-Id': String(user.compId) } : {}) } }
    )
      .then(res => {
        const list = res.data?.data?.workTypeDurations ?? []
        setDurationsByCode(new Map(list.map(d => [d.workTypeCode, d.duration])))
        // SIF-1 '자재파트너가 시공' 공종 식별 — 같은 설정 응답의 발주·입고설정을 교차 조회(실패 시 일반 공종으로 취급)
        const orders = res.data?.data?.orderSettings ?? []
        setConstructCodes(new Set(orders.filter(s => s.constructByVendorYn === 'Y').map(s => s.workTypeCode)))
      })
      .catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.compId])

  // 시작일 선택 시 종료일 기본값 = 시작일 + (선택된 공종의 기본 소요일 - 1)일.
  const handleStartDateChange = (newStart: string) => {
    const dur = durationsByCode.get(form.workTypeCode) ?? 1
    setForm(f => ({ ...f, taskStartDate: newStart, taskEndDate: newStart ? addCalendarDays(newStart, dur - 1) : newStart }))
  }

  const isInvalid =
    !form.workTypeCode ||
    !form.taskStartDate ||
    !form.taskEndDate ||
    form.taskEndDate < form.taskStartDate

  const handleSave = async () => {
    if (!form.workTypeCode)  { setError('공종을 선택하세요'); return }
    if (!form.taskStartDate) { setError('시작일을 입력하세요'); return }
    if (!form.taskEndDate)   { setError('종료일을 입력하세요'); return }
    if (form.taskEndDate < form.taskStartDate) { setError('종료일이 시작일보다 빠를 수 없습니다'); return }
    if (!selectedSite?.scheduleId) return
    const periodPlan = computePeriodSyncPlan(selectedSite, [{
      workTypeCode: form.workTypeCode,
      workTypeName: (codes?.workTypes ?? []).find(w => w.workTypeCode === form.workTypeCode)?.workTypeName ?? '공종',
      startDate: form.taskStartDate,
      endDate: form.taskEndDate,
    }])
    if (periodPlan && !(await periodSync.confirm(periodPlan))) return
    setSaving(true); setError('')
    try {
      const selectedPartner = isConstructByVendor ? undefined : partners.find(p => p.partnerName === form.foremanName)
      await apiClient.post(`/schedule/schedules/${selectedSite.scheduleId}/tasks`, {
        workTypeCode:      form.workTypeCode || undefined,
        assignedForemanId: selectedPartner?.partnerId ?? undefined,
        taskStartDate:     form.taskStartDate,
        taskEndDate:       form.taskEndDate,
        scheduleMemo:      form.description || undefined,
        ...(periodPlan ? { syncSchedulePeriod: true } : {}),
      }, { headers })
      if (periodPlan) refreshSites()
      onSaved()
      onClose()
    } catch {
      setError('저장 중 오류가 발생했습니다.')
    } finally {
      setSaving(false)
    }
  }

  const workTypeOptions = codes?.workTypes ?? []
  // 간트에 이미 있는 공종 — 공종 코드별 개수(같은 공종을 여러 구간으로 둔 경우 'N개 있음')
  const existingCount = new Map<string, number>()
  for (const code of existingWorkTypeCodes ?? []) if (code) existingCount.set(code, (existingCount.get(code) ?? 0) + 1)
  const existingBadge = (code: string) => {
    const n = existingCount.get(code) ?? 0
    return n === 0 ? null : <Badge status="info">{n > 1 ? `간트에 ${n}개 있음` : '간트에 있음'}</Badge>
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'var(--alpha-black-500)', zIndex: Z_POPUP, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        ref={boxRef}
        style={{ background: 'var(--shell-bg)', borderRadius: 24, width: 560, maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: 'var(--elevation-3)' }}
      >
        {/* Header */}
        <div style={{ background: 'var(--shell-surface)', padding: '14px 20px', borderBottom: '1px solid var(--shell-border)', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--gt-primary)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="2"/>
          </svg>
          <span id={titleId} style={{ fontSize: 15, fontWeight: 700, color: 'var(--shell-on-surface)', flex: 1 }}>공종 추가</span>
          {selectedSite?.siteName && <span style={{ fontSize: 12, color: 'var(--shell-on-surface-2)' }}>{selectedSite.siteName}</span>}
          <button
            onClick={onClose}
            aria-label="닫기"
            style={{ width: 28, height: 28, borderRadius: '50%', border: 'none', background: 'var(--gt-subtle)', color: 'var(--gt-primary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, fontFamily: 'inherit', flexShrink: 0, lineHeight: 1 }}
          >×</button>
        </div>

        {/* Form */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
          <div style={{ background: 'var(--shell-surface)', border: '1px solid var(--shell-border)', borderRadius: 16, padding: '24px 28px' }}>

            {/* 공종 */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>공종 <span style={{ color: 'var(--gt-primary)' }}>*</span></label>
              {/* 목록 항목 옆에 간트에 이미 있는 공종 배지 — 이름으로 찾아 고를 수 있게 검색형 */}
              <Combobox
                value={form.workTypeCode}
                displayValue={workTypeOptions.find(w => w.workTypeCode === form.workTypeCode)?.workTypeName ?? ''}
                options={workTypeOptions.map(w => ({ value: w.workTypeCode, label: w.workTypeName }))}
                renderOption={opt => (
                  <span className="flex min-w-0 items-center gap-xs">
                    <span className="min-w-0 truncate">{opt.label}</span>
                    {existingBadge(opt.value)}
                  </span>
                )}
                onChange={val => setForm(f => ({ ...f, workTypeCode: val }))}
                searchable
                placeholder="공종을 선택하세요"
                emptyText="찾는 공종이 없어요"
                aria-label="공종"
                className="!max-w-none"
              />
              {existingCount.has(form.workTypeCode) && (
                <div className="mt-1" style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-hint)' }}>간트에 이미 있는 공종이에요 — 추가하면 같은 공종이 하나 더 생겨요</div>
              )}
            </div>

            {/* 작업반장 */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>작업반장</label>
              {isConstructByVendor ? (
                <Alert kind="info" role="status">자재파트너가 시공하는 공종이라 시공파트너를 지정하지 않아요</Alert>
              ) : (
                <Select
                  value={form.foremanName}
                  onChange={e => setForm(f => ({ ...f, foremanName: e.target.value }))}
                  className="!max-w-none"
                >
                  <option value="">선택 안 함</option>
                  {partners.map(p => (
                    <option key={p.partnerId} value={p.partnerName}>
                      {p.partnerName}{p.mobile ? ` (${p.mobile})` : ''}
                    </option>
                  ))}
                </Select>
              )}
            </div>

            {/* 시작일 + 종료일 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={labelStyle}>시작일 <span style={{ color: 'var(--gt-primary)' }}>*</span></label>
                <DatePicker value={form.taskStartDate} onChange={handleStartDateChange} />
              </div>
              <div>
                <label style={labelStyle}>종료일 <span style={{ color: 'var(--gt-primary)' }}>*</span></label>
                <DatePicker value={form.taskEndDate} onChange={v => setForm(f => ({ ...f, taskEndDate: v }))} />
              </div>
            </div>

            {/* 메모 */}
            <div style={{ marginBottom: 22 }}>
              <label style={labelStyle}>메모</label>
              <Textarea rows={3} placeholder="메모 입력..." value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} maxLength={1000} className="!max-w-none" />
            </div>

            {error && (
              <div className="field error" style={{ marginBottom: 12, maxWidth: 'none' }} role="alert">
                <span className="help">{error}</span>
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Button variant="secondary" size="sm" onClick={onClose}>취소</Button>
              <Button variant="primary" size="sm" loading={saving} disabled={!!isInvalid} onClick={handleSave}>
                {saving ? '저장 중...' : '저장'}
              </Button>
            </div>
          </div>
        </div>
      </div>
      {periodSync.modal}
    </div>
  )
}
